/**
 * Two-factor authentication panel (TOTP).
 *
 * Powers the QR-pairing setup flow + disable-with-current-code flow used by
 * /admin/security and /account/security and /patient/account.
 */
import React, { useEffect, useState } from 'react';
import { KeyRound, AlertTriangle, Check } from 'lucide-react';
import { api } from '../../../lib/api';
import { safeConfirm } from '../../../lib/confirm';

export function TotpPanel() {
  const [enabled, setEnabled] = useState(false);
  const [setup, setSetup] = useState(null); // { otpauth_uri, secret, qr_data_url }
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const reload = () => api.get('/auth/2fa/status').then(r => setEnabled(r.data.enabled));
  useEffect(() => { reload(); }, []);

  const beginSetup = async () => {
    setBusy(true); setMsg('');
    try {
      const { data } = await api.post('/auth/2fa/setup');
      setSetup(data);
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true); setMsg('');
    try {
      await api.post('/auth/2fa/verify', { code });
      setMsg('2FA enabled successfully. Save your backup secret somewhere safe.');
      setSetup(null); setCode('');
      reload();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!await safeConfirm('Disable two-factor auth? Your account will be less secure.')) return;
    const c = window.prompt('Enter a current 2FA code to confirm:');
    if (!c) return;
    setBusy(true); setMsg('');
    try {
      await api.post('/auth/2fa/disable', { code: c });
      setMsg('2FA disabled.');
      reload();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const qrUrl = setup ? (setup.qr_data_url || `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(setup.otpauth_uri)}`) : null;

  return (
    <div className="bento-card">
      <div className="flex items-start gap-4">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${enabled?'bg-[#3A664D]':'bg-[#F2EBDB]'}`}>
          <KeyRound className={`w-5 h-5 ${enabled?'text-white':'text-brand'}`}/>
        </div>
        <div className="flex-1">
          <div className="font-medium" data-testid="totp-state">
            {enabled ? '2FA is ON' : '2FA is off'}
          </div>
          <p className="text-sm text-fg-muted mt-1">
            {enabled
              ? 'Each login requires a 6-digit code from your authenticator app.'
              : 'Pair an authenticator app (Google Authenticator, 1Password, Authy) for an extra layer of security.'}
          </p>
        </div>
        {!enabled && !setup && (
          <button onClick={beginSetup} disabled={busy} className="btn-primary text-sm" data-testid="2fa-setup-button" id="totp-begin">
            Enable 2FA
          </button>
        )}
        {enabled && (
          <button onClick={disable} disabled={busy} className="btn-secondary text-sm text-[#B83A3A] border-[#B83A3A]/30" data-testid="totp-disable">
            Disable
          </button>
        )}
      </div>

      {setup && (
        <div className="mt-5 pt-5 border-t border-soft" data-testid="totp-setup">
          <div className="flex flex-wrap gap-5">
            <img src={qrUrl} alt="2FA QR" className="w-44 h-44 rounded-lg border border-soft p-2 bg-white" data-testid="totp-qr"/>
            <div className="flex-1 min-w-[260px]">
              <div className="text-[10px] uppercase tracking-wider text-fg-muted">Step 1</div>
              <p className="text-sm">Scan the QR with your authenticator app. Or enter this secret manually:</p>
              <div className="font-mono text-xs bg-[#F2EBDB] rounded p-2 my-2 break-all" data-testid="totp-secret">{setup.secret}</div>
              <div className="text-[10px] uppercase tracking-wider text-fg-muted mt-3">Step 2</div>
              <p className="text-sm mb-2">Enter the 6-digit code from the app:</p>
              <input
                value={code}
                onChange={e=>setCode(e.target.value.replace(/\D/g, '').slice(0,6))}
                className="input w-32 text-center font-mono text-lg tracking-[0.4em]"
                placeholder="000000"
                data-testid="totp-code"
              />
              <div className="mt-3 flex gap-2">
                <button onClick={verify} disabled={busy || code.length !== 6} className="btn-primary text-sm" data-testid="totp-verify">
                  Verify & enable
                </button>
                <button onClick={()=>{setSetup(null); setCode('');}} className="btn-secondary text-sm">Cancel</button>
              </div>
            </div>
          </div>
          <div className="mt-4 surface border border-[#FFB000]/30 rounded-lg p-3 flex items-start gap-2 text-xs">
            <AlertTriangle className="w-4 h-4 text-[#FFB000] mt-0.5 shrink-0"/>
            <span><strong>Save the secret!</strong> If you lose your phone, you&apos;ll need it to recover access.</span>
          </div>
        </div>
      )}

      {msg && (
        <div className="mt-4 surface border border-soft rounded-lg p-3 text-sm inline-flex items-center gap-2" data-testid="totp-msg">
          <Check className="w-4 h-4 text-[#3A664D]"/> {msg}
        </div>
      )}
    </div>
  );
}
